import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { Spin } from 'antd';
import { SidebarLayout } from '@/components/Layout/SidebarLayout';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { SmartRedirect } from '@/components/SmartRedirect';
import { isTokenExpiredOrExpiringSoon } from '@/lib/token-refresh';
import { clearAuthStorage } from '@/lib/auth-session';

// Lazy load each page file so sibling screens in a folder are not bundled together
const Home = lazy(() => import('@/pages/Home/Home').then(m => ({ default: m.Home })));
const Login = lazy(() => import('@/pages/Login/Login').then(m => ({ default: m.Login })));
const ForgotPassword = lazy(() => import('@/pages/ForgotPassword/ForgotPassword').then(m => ({ default: m.ForgotPassword })));
const ResetPassword = lazy(() => import('@/pages/ResetPassword/ResetPassword').then(m => ({ default: m.ResetPassword })));
const CreateAccount = lazy(() => import('@/pages/CreateAccount/CreateAccount').then(m => ({ default: m.CreateAccount })));
const Orders = lazy(() => import('@/pages/Orders/Orders').then(m => ({ default: m.Orders })));
const OrderDetail = lazy(() => import('@/pages/OrderDetail/OrderDetail').then(m => ({ default: m.OrderDetail })));
const CreateOrder = lazy(() => import('@/pages/CreateOrder/CreateOrder').then(m => ({ default: m.CreateOrder })));
const ReviewOrder = lazy(() => import('@/pages/ReviewOrder/ReviewOrder').then(m => ({ default: m.ReviewOrder })));
const Inventory = lazy(() => import('@/pages/Inventory/Inventory').then(m => ({ default: m.Inventory })));
const InventoryHealth = lazy(() => import('@/pages/InventoryHealth/InventoryHealth').then(m => ({ default: m.InventoryHealth })));
const InventoryDetail = lazy(() => import('@/pages/InventoryDetail/InventoryDetail').then(m => ({ default: m.InventoryDetail })));
const Lots = lazy(() => import('@/pages/Lots/Lots').then(m => ({ default: m.Lots })));
const LotRecall = lazy(() => import('@/pages/LotRecall/LotRecall').then(m => ({ default: m.LotRecall })));
const CycleCounts = lazy(() => import('@/pages/CycleCount/CycleCounts').then(m => ({ default: m.CycleCounts })));
const CycleCountCreate = lazy(() => import('@/pages/CycleCount/CycleCount').then(m => ({ default: m.CycleCount })));
const CycleCountDetail = lazy(() => import('@/pages/CycleCount/CycleCountDetail').then(m => ({ default: m.CycleCountDetail })));
const WarehouseInventoryPage = lazy(() => import('@/pages/WarehouseInventory/WarehouseInventory').then(m => ({ default: m.WarehouseInventory })));
const WarehouseInventoryDetailPage = lazy(() => import('@/pages/WarehouseInventoryDetail/WarehouseInventoryDetail').then(m => ({ default: m.WarehouseInventoryDetail })));
const AddressBook = lazy(() => import('@/pages/AddressBook/AddressBook').then(m => ({ default: m.AddressBook })));
const AddressBookEntryForm = lazy(() => import('@/pages/AddressBook/AddressBookEntryForm').then(m => ({ default: m.AddressBookEntryForm })));
const CSVUploadMapping = lazy(() => import('@/pages/CSVUploadMapping/CSVUploadMapping').then(m => ({ default: m.CSVUploadMapping })));
const Users = lazy(() => import('@/pages/Users/Users').then(m => ({ default: m.Users })));
const UserDetail = lazy(() => import('@/pages/Users/UserDetail').then(m => ({ default: m.UserDetail })));
const Messages = lazy(() => import('@/pages/Messages/Messages').then(m => ({ default: m.Messages })));
const MessageDetail = lazy(() => import('@/pages/Messages/MessageDetail').then(m => ({ default: m.MessageDetail })));
const HolidayCalendar = lazy(() => import('@/pages/HolidayCalendar/HolidayCalendar').then(m => ({ default: m.HolidayCalendar })));
const Loadouts = lazy(() => import('@/pages/Loadouts/Loadouts').then(m => ({ default: m.Loadouts })));
const LoadoutDetail = lazy(() => import('@/pages/Loadouts/LoadoutDetail').then(m => ({ default: m.LoadoutDetail })));
const Perishability = lazy(() => import('@/pages/Perishability/Perishability').then(m => ({ default: m.Perishability })));
const PerishabilityDetail = lazy(() => import('@/pages/Perishability/PerishabilityDetail').then(m => ({ default: m.PerishabilityDetail })));
const Packaging = lazy(() => import('@/pages/Packaging/Packaging').then(m => ({ default: m.Packaging })));
const Optimizer = lazy(() => import('@/pages/Optimizer/Optimizer').then(m => ({ default: m.Optimizer })));
const SkuTranslations = lazy(() => import('@/pages/SkuTranslations/SkuTranslations').then(m => ({ default: m.SkuTranslations })));
const SkuTranslationDetail = lazy(() => import('@/pages/SkuTranslations/SkuTranslationDetail').then(m => ({ default: m.SkuTranslationDetail })));
const InboundRequests = lazy(() => import('@/pages/InboundRequests/InboundRequests').then(m => ({ default: m.InboundRequests })));
const InboundRequestDetail = lazy(() => import('@/pages/InboundRequests/InboundRequestDetail').then(m => ({ default: m.InboundRequestDetail })));
const Outbounds = lazy(() => import('@/pages/Outbounds/Outbounds').then(m => ({ default: m.Outbounds })));
const OutboundDetail = lazy(() => import('@/pages/Outbounds/OutboundDetail').then(m => ({ default: m.OutboundDetail })));
const Merchants = lazy(() => import('@/pages/Merchants/Merchants').then(m => ({ default: m.Merchants })));
const MerchantDetail = lazy(() => import('@/pages/Merchants/MerchantDetail').then(m => ({ default: m.MerchantDetail })));
const OrdersReport = lazy(() => import('@/pages/Reports/OrdersReport').then(m => ({ default: m.OrdersReport })));
const CustomOrderReport = lazy(() => import('@/pages/Reports/CustomOrderReport').then(m => ({ default: m.CustomOrderReport })));
const ShippingReport = lazy(() => import('@/pages/Reports/ShippingReport').then(m => ({ default: m.ShippingReport })));
const InventoryActivityReport = lazy(() => import('@/pages/Reports/InventoryActivityReport').then(m => ({ default: m.InventoryActivityReport })));
const ProductVolumeReport = lazy(() => import('@/pages/Reports/ProductVolumeReport').then(m => ({ default: m.ProductVolumeReport })));
const MonthlyVolumeReport = lazy(() => import('@/pages/Reports/MonthlyVolumeReport').then(m => ({ default: m.MonthlyVolumeReport })));
const SalesSampleReport = lazy(() => import('@/pages/Reports/SalesSampleReport').then(m => ({ default: m.SalesSampleReport })));
const WarehouseLotReport = lazy(() => import('@/pages/Reports/WarehouseLotReport').then(m => ({ default: m.WarehouseLotReport })));
const WarehouseInventoryReport = lazy(() => import('@/pages/Reports/WarehouseInventoryReport').then(m => ({ default: m.WarehouseInventoryReport })));
const PackingReport = lazy(() => import('@/pages/Reports/PackingReport').then(m => ({ default: m.PackingReport })));
const GrievanceSummaryReport = lazy(() => import('@/pages/Reports/GrievanceSummaryReport').then(m => ({ default: m.GrievanceSummaryReport })));
const CarrierPerformanceReport = lazy(() => import('@/pages/Reports/CarrierPerformanceReport').then(m => ({ default: m.CarrierPerformanceReport })));
const AssetManagement = lazy(() => import('@/pages/AssetManagement/AssetManagement').then(m => ({ default: m.AssetManagement })));
const Dashboard = lazy(() => import('@/pages/Dashboard/Dashboard').then(m => ({ default: m.Dashboard })));
const BillingEvents = lazy(() => import('@/pages/Billing/BillingEvents').then(m => ({ default: m.BillingEvents })));
const BillingRuns = lazy(() => import('@/pages/Billing/BillingRuns').then(m => ({ default: m.BillingRuns })));
const BillingRunDetail = lazy(() => import('@/pages/Billing/BillingRunDetail').then(m => ({ default: m.BillingRunDetail })));
const BillsList = lazy(() => import('@/pages/Billing/Bills').then(m => ({ default: m.Bills })));
const BillDetail = lazy(() => import('@/pages/Billing/BillDetail').then(m => ({ default: m.BillDetail })));
const BillingFailures = lazy(() => import('@/pages/Billing/BillingFailures').then(m => ({ default: m.BillingFailures })));
const NotFound = lazy(() => import('@/pages/NotFound/NotFound').then(m => ({ default: m.NotFound })));

// Loading fallback component
const PageLoader = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
    <Spin size="large" />
  </div>
);

// Component to redirect authenticated users away from login page
function LoginRedirect() {
  const token = localStorage.getItem('auth_token');
  const user = localStorage.getItem('auth_user');
  
  // Only redirect if token exists, is valid (not expired), and user exists
  if (token && user && !isTokenExpiredOrExpiringSoon(token, 0)) {
    // Use SmartRedirect to route to first available page based on permissions
    return (
      <ProtectedRoute>
        <SmartRedirect />
      </ProtectedRoute>
    );
  }
  
  // Clear any expired tokens
  if (token && isTokenExpiredOrExpiringSoon(token, 0)) {
    clearAuthStorage();
  }
  
  return (
    <Suspense fallback={<PageLoader />}>
      <Login />
    </Suspense>
  );
};

export const router = createBrowserRouter([
  {
    path: '/login',
    element: <LoginRedirect />,
  },
  {
    path: '/forgot-password',
    element: (
      <Suspense fallback={<PageLoader />}>
        <ForgotPassword />
      </Suspense>
    ),
  },
  {
    path: '/reset-password',
    element: (
      <Suspense fallback={<PageLoader />}>
        <ResetPassword />
      </Suspense>
    ),
  },
  {
    path: '/create-account',
    element: (
      <Suspense fallback={<PageLoader />}>
        <CreateAccount />
      </Suspense>
    ),
  },
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <SidebarLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: <SmartRedirect />,
      },
      {
        path: 'dashboard',
        element: (
          <ProtectedRoute requiredPermission="system.dashboard.view">
            <Suspense fallback={<PageLoader />}>
              <Dashboard />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'orders',
        element: (
          <ProtectedRoute requiredPermission="orders.actions.view">
            <Suspense fallback={<PageLoader />}>
              <Orders />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'orders/create',
        element: (
          <ProtectedRoute requiredPermission="orders.actions.edit">
            <Suspense fallback={<PageLoader />}>
              <CreateOrder />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'orders/review',
        element: (
          <ProtectedRoute requiredPermission="orders.actions.edit">
            <Suspense fallback={<PageLoader />}>
              <ReviewOrder />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'orders/csv-upload',
        element: (
          <ProtectedRoute requiredPermission="orders.actions.csvUpload">
            <Suspense fallback={<PageLoader />}>
              <CSVUploadMapping />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'orders/address-book',
        element: (
          <ProtectedRoute requiredPermission="addressBook.actions.view" requireMerchantContext>
            <Suspense fallback={<PageLoader />}>
              <AddressBook />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'orders/address-book/new',
        element: (
          <ProtectedRoute requiredPermission="addressBook.actions.create" requireMerchantContext>
            <Suspense fallback={<PageLoader />}>
              <AddressBookEntryForm />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'orders/address-book/:id',
        element: (
          <ProtectedRoute requiredPermission="addressBook.actions.edit" requireMerchantContext>
            <Suspense fallback={<PageLoader />}>
              <AddressBookEntryForm />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'orders/:transactionId',
        element: (
          <ProtectedRoute requiredPermission="orders.actions.view">
            <Suspense fallback={<PageLoader />}>
              <OrderDetail />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'inventory',
        element: (
          <ProtectedRoute requiredPermission="inventory.actions.view">
            <Suspense fallback={<PageLoader />}>
              <Inventory />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'inventory/health',
        element: (
          <ProtectedRoute requiredPermission="inventory.actions.viewHealth">
            <Suspense fallback={<PageLoader />}>
              <InventoryHealth />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'inventory/lots',
        element: (
          <ProtectedRoute requiredPermission="inventory.actions.viewLots">
            <Suspense fallback={<PageLoader />}>
              <Lots />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'inventory/cycle-count',
        element: (
          <ProtectedRoute requiredPermission="inventory.actions.viewCycleCount">
            <Suspense fallback={<PageLoader />}>
              <CycleCounts />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'inventory/cycle-count/new',
        element: (
          <ProtectedRoute requiredPermission="inventory.actions.viewCycleCount">
            <Suspense fallback={<PageLoader />}>
              <CycleCountCreate />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'inventory/cycle-count/:id',
        element: (
          <ProtectedRoute requiredPermission="inventory.actions.viewCycleCount">
            <Suspense fallback={<PageLoader />}>
              <CycleCountDetail />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'inventory/lot-recall',
        element: (
          <ProtectedRoute requiredPermission="lotRecall.actions.view">
            <Suspense fallback={<PageLoader />}>
              <LotRecall />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'inventory/warehouse',
        element: (
          <ProtectedRoute requiredPermission="inventory.actions.view">
            <Suspense fallback={<PageLoader />}>
              <WarehouseInventoryPage />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'inventory/warehouse/:id',
        element: (
          <ProtectedRoute requiredPermission="inventory.actions.view">
            <Suspense fallback={<PageLoader />}>
              <WarehouseInventoryDetailPage />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'inventory/:sku',
        element: (
          <ProtectedRoute requiredPermission="inventory.actions.view">
            <Suspense fallback={<PageLoader />}>
              <InventoryDetail />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'inventory/sku-translations',
        element: (
          <ProtectedRoute requiredPermission="inventory.actions.skuTranslations.view">
            <Suspense fallback={<PageLoader />}>
              <SkuTranslations />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'inventory/sku-translations/:id',
        element: (
          <ProtectedRoute requiredPermission="inventory.actions.skuTranslations.view">
            <Suspense fallback={<PageLoader />}>
              <SkuTranslationDetail />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'inventory/inbounds',
        element: (
          <ProtectedRoute requiredPermission="inventory.actions.viewInbound">
            <Suspense fallback={<PageLoader />}>
              <InboundRequests />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'inventory/inbounds/:shippingRequestNumber',
        element: (
          <ProtectedRoute requiredPermission="inventory.actions.viewInbound">
            <Suspense fallback={<PageLoader />}>
              <InboundRequestDetail />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'inventory/outbounds',
        element: (
          <ProtectedRoute requiredPermission="inventory.actions.viewOutbound">
            <Suspense fallback={<PageLoader />}>
              <Outbounds />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'inventory/outbounds/:outboundId',
        element: (
          <ProtectedRoute requiredPermission="inventory.actions.viewOutbound">
            <Suspense fallback={<PageLoader />}>
              <OutboundDetail />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'merchants',
        element: (
          <ProtectedRoute requiredPermission="merchants.actions.view">
            <Suspense fallback={<PageLoader />}>
              <Merchants />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'merchants/:id',
        element: (
          <ProtectedRoute requiredPermission="merchants.actions.view">
            <Suspense fallback={<PageLoader />}>
              <MerchantDetail />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'system/users',
        element: (
          <ProtectedRoute requiredPermission="users.actions.manageUsers">
            <Suspense fallback={<PageLoader />}>
              <Users />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'system/users/:id',
        element: (
          <ProtectedRoute requiredPermission="users.actions.manageUsers">
            <Suspense fallback={<PageLoader />}>
              <UserDetail />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'reports',
        element: (
          <ProtectedRoute requiredPermission="reports.actions.view">
            <Navigate to="/reports/orders" replace />
          </ProtectedRoute>
        ),
      },
      {
        path: 'reports/orders',
        element: (
          <ProtectedRoute requiredPermission="reports.actions.view">
            <Suspense fallback={<PageLoader />}>
              <OrdersReport />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'reports/custom-orders',
        element: (
          <ProtectedRoute requiredPermission="reports.actions.view">
            <Suspense fallback={<PageLoader />}>
              <CustomOrderReport />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'reports/shipping',
        element: (
          <ProtectedRoute requiredPermission="reports.actions.view">
            <Suspense fallback={<PageLoader />}>
              <ShippingReport />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'reports/inventory-activity',
        element: (
          <ProtectedRoute requiredPermission="reports.actions.view">
            <Suspense fallback={<PageLoader />}>
              <InventoryActivityReport />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'reports/product-volume',
        element: (
          <ProtectedRoute requiredPermission="reports.actions.view">
            <Suspense fallback={<PageLoader />}>
              <ProductVolumeReport />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'reports/monthly-volume',
        element: (
          <ProtectedRoute requiredPermission="reports.actions.view">
            <Suspense fallback={<PageLoader />}>
              <MonthlyVolumeReport />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'reports/sales-samples',
        element: (
          <ProtectedRoute requiredPermission="reports.actions.view">
            <Suspense fallback={<PageLoader />}>
              <SalesSampleReport />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'reports/warehouse-lots',
        element: (
          <ProtectedRoute requiredPermission="reports.actions.view">
            <Suspense fallback={<PageLoader />}>
              <WarehouseLotReport />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'reports/warehouse-inventory',
        element: (
          <ProtectedRoute requiredPermission="reports.actions.view">
            <Suspense fallback={<PageLoader />}>
              <WarehouseInventoryReport />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'reports/packing',
        element: (
          <ProtectedRoute requiredPermission="reports.actions.view">
            <Suspense fallback={<PageLoader />}>
              <PackingReport />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'reports/grievance-summary',
        element: (
          <ProtectedRoute requiredPermission="reports.actions.performance.view">
            <Suspense fallback={<PageLoader />}>
              <GrievanceSummaryReport />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'reports/carrier-performance',
        element: (
          <ProtectedRoute requiredPermission="reports.actions.performance.view">
            <Suspense fallback={<PageLoader />}>
              <CarrierPerformanceReport />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'system/messages',
        element: (
          <ProtectedRoute requiredPermission="system.messages.view">
            <Suspense fallback={<PageLoader />}>
              <Messages />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'system/messages/:id',
        element: (
          <ProtectedRoute requiredPermission="system.messages.edit">
            <Suspense fallback={<PageLoader />}>
              <MessageDetail />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'system/holiday-calendar',
        element: (
          <ProtectedRoute requiredPermission="system.holidayCalendar.view">
            <Suspense fallback={<PageLoader />}>
              <HolidayCalendar />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'packaging/loadouts',
        element: (
          <ProtectedRoute requiredPermission="system.loadouts.view">
            <Suspense fallback={<PageLoader />}>
              <Loadouts />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'packaging/loadouts/:id',
        element: (
          <ProtectedRoute requiredPermission="system.loadouts.view">
            <Suspense fallback={<PageLoader />}>
              <LoadoutDetail />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'packaging/perishability',
        element: (
          <ProtectedRoute requiredPermission="system.perishability.view">
            <Suspense fallback={<PageLoader />}>
              <Perishability />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'packaging/perishability/:id',
        element: (
          <ProtectedRoute requiredPermission="system.perishability.view">
            <Suspense fallback={<PageLoader />}>
              <PerishabilityDetail />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'packaging/boxes-coolers',
        element: (
          <ProtectedRoute requiredPermission="system.packaging.view">
            <Suspense fallback={<PageLoader />}>
              <Packaging />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'packaging/optimizer',
        element: (
          <ProtectedRoute requiredPermission="system.packaging.view">
            <Suspense fallback={<PageLoader />}>
              <Optimizer />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'system/assets',
        element: (
          <ProtectedRoute requiredPermission="assets.view">
            <Suspense fallback={<PageLoader />}>
              <AssetManagement />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'billing/events',
        element: (
          <ProtectedRoute requiredPermission="billing.actions.view">
            <Suspense fallback={<PageLoader />}>
              <BillingEvents />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'billing/runs',
        element: (
          <ProtectedRoute requiredPermission="billing.actions.view">
            <Suspense fallback={<PageLoader />}>
              <BillingRuns />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'billing/runs/:id',
        element: (
          <ProtectedRoute requiredPermission="billing.actions.view">
            <Suspense fallback={<PageLoader />}>
              <BillingRunDetail />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'billing/bills',
        element: (
          <ProtectedRoute requiredPermission="billing.actions.view">
            <Suspense fallback={<PageLoader />}>
              <BillsList />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'billing/bills/:id',
        element: (
          <ProtectedRoute requiredPermission="billing.actions.view">
            <Suspense fallback={<PageLoader />}>
              <BillDetail />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'billing/failures',
        element: (
          <ProtectedRoute requiredPermission="billing.actions.view">
            <Suspense fallback={<PageLoader />}>
              <BillingFailures />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'home',
        element: (
          <ProtectedRoute>
            <Suspense fallback={<PageLoader />}>
              <Home />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: '*',
        element: (
          <Suspense fallback={<PageLoader />}>
            <NotFound />
          </Suspense>
        ),
      },
    ],
  },
]);

